import React, { useState } from 'react';
import classNames from 'classnames';
import { useIntl } from '../../util/reactIntl';
import {
  IconClose,
  IconCheckmark,
  Button,
  InlineTextButton,
  Menu,
  MenuLabel,
  MenuItem,
  MenuContent,
  Modal,
} from '../../components';

import IconCard from './IconCard/IconCard';
import css from './CustomSavedCardDetails.module.css';

const DEFAULT_CARD = 'defaultCard';
const REPLACE_CARD = 'replaceCard';

/**
 * A component that renders a saved card details.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Object} props.card - The card to render
 * @param {string} props.card.brand - The brand of the card
 * @param {number} props.card.expirationMonth - The expiration month of the card
 * @param {number} props.card.expirationYear - The expiration year of the card
 * @param {string} props.card.last4Digits - The last 4 digits of the card
 * @param {function} [props.onChange] - The function to call when the card is changed
 * @param {function} [props.onDeleteCard] - The function to call when the card is deleted
 * @param {function} [props.onManageDisableScrolling] - The function to call when the modal is opened
 * @param {boolean} [props.deletePaymentMethodInProgress] - Whether the delete payment method is in progress
 * @returns {JSX.Element}
 */
const CustomSavedCardDetails = props => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState(DEFAULT_CARD);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const intl = useIntl();

  const {
    rootClassName,
    className,
    card,
    onChange,
    onDeleteCard,
    onManageDisableScrolling,
    deletePaymentMethodInProgress,
  } = props;


  const { last4Digits, expirationMonth, expirationYear, brand } = card || {};
  const classes = classNames(rootClassName || css.root, className);

  const paymentMethodPlaceholderDesktop = intl.formatMessage(
    { id: 'CustomSavedCardDetails.savedPaymentMethodPlaceholderDesktop' },
    { last4Digits }
  );

  const paymentMethodPlaceholderMobile = intl.formatMessage(
    { id: 'CustomSavedCardDetails.savedPaymentMethodPlaceholderMobile' },
    { last4Digits }
  );

  const paymentMethodPlaceholder = (
    <>
      <span className={css.paymentMethodPlaceholderDesktop}>{paymentMethodPlaceholderDesktop}</span>
      <span className={css.paymentMethodPlaceholderMobile}>{paymentMethodPlaceholderMobile}</span>
    </>
  );

  const replaceCardText = intl.formatMessage({
    id: 'CustomSavedCardDetails.replaceCardText',
  });
  const replaceCard = (
    <span>
      <IconCard brand="none" className={css.cardIcon} /> {replaceCardText}
    </span>
  );

  const expiredCardText = intl.formatMessage(
    { id: 'CustomSavedCardDetails.expiredCardText' },
    { last4Digits }
  );
  const expiredText = <div className={css.cardExpiredText}>{expiredCardText}</div>;

  const isExpired = (expirationMonth, expirationYear) => {
    const currentTime = new Date();
    const currentYear = currentTime.getFullYear();
    const currentMonth = currentTime.getMonth() + 1; //getMonth() method returns the month (from 0 to 11)

    if (expirationYear < currentYear) {
      return true;
    } else if (expirationYear === currentYear && expirationMonth < currentMonth) {
      return true;
    }

    return false;
  };

  const isCardExpired =
    expirationMonth && expirationYear && isExpired(expirationMonth, expirationYear);

  const defaultCard = (
    <div className={css.savedPaymentMethod}>
      <IconCard brand={brand} className={css.cardIcon} />
      {paymentMethodPlaceholder}
      <span className={isCardExpired ? css.expirationDateExpired : css.expirationDate}>
        {/* {expirationMonth}/{expirationYear.toString().substring(2)} */}
      </span>
    </div>
  );

  const handleClick = item => e => {
    // Clicking buttons inside a form will call submit
    e.preventDefault();
    e.stopPropagation();

    setActive(item);
    setMenuOpen(false);
    if (onChange) {
      onChange(item);
    }
  };

  const onToggleActive = isOpen => {
    setMenuOpen(isOpen);
    if (!hasBeenOpened && !isOpen) {
      setHasBeenOpened(true);
    }
  };

  const handleDeleteCard = () => {
    setIsModalOpen(true);
  };

  const handleAddPaymentMethod = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setActive(REPLACE_CARD);
    
    if (onChange) {
      onChange(REPLACE_CARD);
    }
  };

  const replaceCardTitle = intl.formatMessage({
    id: 'CustomSavedCardDetails.replaceCardTitle',
  });
  const removeCardModalTitle = intl.formatMessage({
    id: 'CustomSavedCardDetails.removeCardModalTitle',
  });
  const removeCardModalContent = intl.formatMessage(
    { id: 'CustomSavedCardDetails.removeCardModalContent' },
    { last4Digits }
  );
  const cancel = intl.formatMessage({ id: 'CustomSavedCardDetails.cancel' });
  const removeCard = intl.formatMessage({ id: 'CustomSavedCardDetails.removeCard' });
  const deletePaymentMethod = intl.formatMessage({
    id: 'CustomSavedCardDetails.deletePaymentMethod',
  });
  const addPaymentMethodText = intl.formatMessage({
    id: 'CustomSavedCardDetails.addPaymentMethod',
  });

  const showExpired = isCardExpired && active === DEFAULT_CARD;

  const useDefaultMethodText = intl.formatMessage({
    id: 'CustomSavedCardDetails.useDefaultMethod',
  });

  return (
    <div className={classes}>
      <div className={css.paymentContainer}>
        {/* Siempre mostrar la tarjeta por defecto */}
        <div className={showExpired && css.menuLabelWrapperExpired}>
          {defaultCard}
        </div>

        {/* Mostrar el botón según el estado activo */}
        <>
          <hr />
          <button 
            onClick={active === DEFAULT_CARD ? handleAddPaymentMethod : handleClick(DEFAULT_CARD)} 
            className={css.addPaymentMethodButton}
            type="button"
          >
            {active === DEFAULT_CARD 
              ? (addPaymentMethodText || 'Add payment method')
              : (useDefaultMethodText || 'Use default payment method')
            }
          </button>
        </>

        {showExpired && !menuOpen ? expiredText : null}

        {onManageDisableScrolling ? (
          <Modal
            id="VerifyDeletingPaymentMethod"
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
            }}
            usePortal
            contentClassName={css.modalContent}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div>
              <div className={css.modalTitle}>{removeCardModalTitle}</div>
              <p className={css.modalMessage}>{removeCardModalContent}</p>
              <div className={css.modalButtonsWrapper}>
                <div
                  onClick={() => setIsModalOpen(false)}
                  className={css.cancelCardDelete}
                  tabIndex="0"
                >
                  {cancel}
                </div>
                <Button onClick={onDeleteCard} inProgress={deletePaymentMethodInProgress}>
                  {removeCard}
                </Button>
              </div>
            </div>
          </Modal>
        ) : null}
      </div>
    </div>
  );
};

export default CustomSavedCardDetails;